import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Copy, Link2, Mail } from 'lucide-react';
import { toast } from 'react-toastify';
import { checkClientEmail, companySiteReadStream, createClient, generateIntakeLink, sendIntakeInvite } from '../../services/api';
import { aaiApi } from '../../services/aaiApi';
import { useActiveClient } from '../../contexts/ActiveClientContext';
import { OrbitDeck } from '../../components/orbit-deck/OrbitDeck';
import { fillPasses, mergePass, READ_PASSES, stopWork } from '../../components/orbit-deck/readPasses';
import type { DeckAnswer, DeckBodyContext, DeckCardModel, DeckContextItem, DeckContextState, DeckLearned, DeckWork, DeckWorkStep } from '../../components/orbit-deck/types';
import type { OrbitDeckResponse, OrbitOverview, OrbitRoomPerson } from '../../types/aai';
import type { ReadPass, ReadStep } from '../../services/api';
import { Action, Chip, ErrorNote, Field, Input, Loading, PageBody, PageHead, Panel, PanelHead } from '../../components/aa';
import { ingest } from '../../orbit/lib/intakeApi';
import { hostOf, siteRoot } from '../../lib/site';
import { askIdOf, railFromOverview, readDocument, searchToolsFor, toDeckCards, transcribeFor } from '../orbit-deck/deckModel';

/*
 * Add a client: no wizard. A short form creates the client, then the partner
 * is dealt the deck on the owner's behalf. The first card is theirs alone:
 * which opening questions this client gets. Then those cards, each ending as
 * "Answer for the client" (saved with the partner as source, shown to the
 * owner as to confirm) or "Leave for the owner" (one of the owner's first
 * cards). Documents dropped on any card are read at once. The last card is
 * the invite, and the deck lands on the client's company page with phase 1 open.
 */

interface Created { clientId: string; company: string; email: string; website: string; }

/* Where the partner is in this client's setup. The framework card is answered
   once and never again, so a deck picked back up does not ask for it twice. */
type Stage = 'framework' | 'deck';

/*
 * The deck lives in this component's state, which is fine until the partner
 * leaves to adjust the questions. Without somewhere to put the client, coming
 * back to this route would show the empty form again and the next submit would
 * create a SECOND client. So the client is written down the moment it exists,
 * and the route resumes from ?client_id= instead of starting over.
 */
const STASH = 'aa_add_client';

const stash = (created: Created, stage: Stage): void => {
  try {
    localStorage.setItem(`${STASH}:${created.clientId}`, JSON.stringify({ ...created, stage }));
  } catch {
    // Storage may be blocked. Leaving the page then loses the deck, which the
    // resume path below reports rather than turning into a duplicate client.
  }
};

const unstash = (clientId: string): { created: Created; stage: Stage } | null => {
  try {
    const raw = localStorage.getItem(`${STASH}:${clientId}`);
    if (!raw) return null;
    const row = JSON.parse(raw) as Created & { stage?: Stage };
    if (!row.clientId) return null;
    let stage: Stage = 'framework';
    if (row.stage === 'deck') stage = 'deck';
    return { created: { clientId: row.clientId, company: row.company, email: row.email, website: row.website }, stage };
  } catch {
    return null;
  }
};

const dropStash = (clientId: string): void => {
  try {
    localStorage.removeItem(`${STASH}:${clientId}`);
  } catch {
    // Nothing to clean up that matters.
  }
};

const LINK_DAYS = 14;
const READ_CARD = 'site_read';
/* The read owns its card for the walk through its seven passes, which the deck
   paces itself. This is only the backstop for a walk that never reports. */
const HANDOFF_CAP = 45000;
const LANDED = ['done', 'empty', 'failed'];

/** The route back to this client's deck, for a page the partner steps out to. */
const deckPath = (clientId: string): string => `/partner/clients/new?client_id=${encodeURIComponent(clientId)}`;

/** The framework, opened for one client, carrying the way back with it. */
const frameworkPath = (clientId: string, company: string, back: string): string => {
  const base = `/partner/framework?client_id=${encodeURIComponent(clientId)}&client_name=${encodeURIComponent(company)}`;
  if (!back) return base;
  return `${base}&back=${encodeURIComponent(back)}`;
};

const FrameworkBody: React.FC<{ clientId: string; company: string } & DeckBodyContext> = ({ clientId, company, answer, busy }) => {
  const navigate = useNavigate();
  return (
    <div className="odeck-free col">
      <button type="button" className="go wide" disabled={busy} onClick={() => answer({ how: 'picked', value: 'keep' })}>Use my questions as they are</button>
      <button type="button" className="odeck-chip dashed" style={{ justifyContent: 'center' }} disabled={busy} onClick={() => navigate(frameworkPath(clientId, company, deckPath(clientId)))}>Adjust this client's questions first</button>
      <small style={{ fontSize: 12, color: 'var(--aa-fg-2)' }}>The questions open on their own page, with a way back to this deck. Nothing here is lost.</small>
    </div>
  );
};

const InviteBody: React.FC<{ created: Created } & DeckBodyContext> = ({ created, answer, skip, busy }) => {
  const [link, setLink] = useState<string | null>(null);
  const [minting, setMinting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const copy = async () => {
    setMinting(true);
    try {
      const url = link || (await generateIntakeLink(created.clientId, LINK_DAYS)).url;
      setLink(url);
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success('Invite link copied.');
      } catch {
        toast.info('The link is ready. Select it and copy it yourself.');
      }
    } catch (err) {
      toast.error((err as Error).message || 'Could not prepare the invite link.');
    } finally {
      setMinting(false);
    }
  };
  const send = async () => {
    setSending(true);
    try {
      const res = await sendIntakeInvite(created.clientId, LINK_DAYS);
      toast.success(`Invite sent to ${res.email}.`);
      answer({ how: 'given', value: 'sent' });
    } catch (err) {
      toast.error((err as Error).message || 'Could not send the invite.');
    } finally {
      setSending(false);
    }
  };
  let copyWord = 'Copy link';
  if (minting) copyWord = 'Preparing';
  if (copied) copyWord = 'Copied';
  return (
    <div className="odeck-free col">
      <div className="flex items-center gap-3 rounded-card bg-ink px-4 py-3 text-white">
        <Link2 size={16} strokeWidth={1.75} className="shrink-0 text-[#adadad]" />
        <span className={`min-w-0 flex-1 truncate text-[12.5px] ${link ? '' : 'text-[#adadad]'}`}>{link || `Bound to ${created.company} only. Expires in ${LINK_DAYS} days.`}</span>
        <button type="button" onClick={copy} disabled={minting || busy} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-accent-500 px-3 text-[12px] font-semibold text-black disabled:opacity-45 whitespace-nowrap">
          {copied ? <Check size={14} strokeWidth={2.5} /> : <Copy size={14} strokeWidth={2} />}{copyWord}
        </button>
      </div>
      <button type="button" className="go wide" disabled={sending || busy} onClick={send}><Mail size={15} className="mr-2 inline" />{sending ? 'Sending' : `Send the invite to ${created.email}`}</button>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <small style={{ fontSize: 12, color: 'var(--aa-fg-2)' }}>Copying the link also marks the client Invited.</small>
        <div className="flex gap-2">
          {link && <button type="button" className="odeck-chip" disabled={busy} onClick={() => answer({ how: 'given', value: 'copied' })}>Done, I shared the link</button>}
          <button type="button" className="odeck-chip" disabled={busy} onClick={skip}>Later, from the Overview</button>
        </div>
      </div>
    </div>
  );
};

const StartForm: React.FC<{ onCreated: (c: Created) => void }> = ({ onCreated }) => {
  const { adopt } = useActiveClient();
  const [company, setCompany] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailCheck, setEmailCheck] = useState<{ state: 'idle' | 'checking' | 'ok' | 'invalid' | 'taken' | 'conflict'; message: string }>({ state: 'idle', message: '' });

  const emailShape = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;

  // The check runs as the partner types, so a taken email or a domain that
  // already belongs to a client is caught on the spot, not on submit.
  useEffect(() => {
    const address = ownerEmail.trim().toLowerCase();
    if (!address) { setEmailCheck({ state: 'idle', message: '' }); return; }
    if (!emailShape.test(address)) { setEmailCheck({ state: 'invalid', message: 'That does not look like an email address.' }); return; }
    setEmailCheck({ state: 'checking', message: 'Checking' });
    let live = true;
    const t = window.setTimeout(async () => {
      try {
        const res = await checkClientEmail(address);
        if (!live) return;
        if (res.email_taken) { setEmailCheck({ state: 'taken', message: 'This email already has an account. Invite that person from their company, or use another address.' }); return; }
        if (res.conflict) { setEmailCheck({ state: 'conflict', message: `${res.conflict.reason}. ${res.conflict.remedy}` }); return; }
        setEmailCheck({ state: 'ok', message: 'Available. The domain is not registered yet.' });
      } catch {
        if (live) setEmailCheck({ state: 'ok', message: '' });
      }
    }, 450);
    return () => { live = false; window.clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerEmail]);

  /* Off unless the partner says otherwise. Creating a record and telling the
     company about it are two decisions, and a prospect they are still working
     should not learn they exist in a system because somebody filled a form. */
  const [sendEmail, setSendEmail] = useState(false);
  const emailOk = emailCheck.state === 'ok';
  const valid = company.trim().length > 1 && emailOk;
  let emailHint: React.ReactNode = 'Used to protect company ownership. The invite goes to this address when you send it.';
  if (emailCheck.state === 'checking') emailHint = 'Checking the address';
  if (emailCheck.state === 'ok' && emailCheck.message) emailHint = <span className="text-[#5da14a]">{emailCheck.message}</span>;
  if (emailCheck.state === 'invalid' || emailCheck.state === 'taken' || emailCheck.state === 'conflict') emailHint = <span className="text-[#c8102e]">{emailCheck.message}</span>;
  let reason = 'Give the company a name and the owner an email first.';
  if (company.trim().length > 1 && !emailOk) reason = emailCheck.message || 'Give the owner a valid email first.';

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const site = siteRoot(website);
      const res = await createClient({
        name: company.trim(),
        email: ownerEmail.trim(),
        contact_name: ownerName.trim() || undefined,
        phone: ownerPhone.trim() || undefined,
        // Read in the background: the partner goes straight to the client's
        // Room, which shows what the read found as it lands.
        ...(site ? { website: site } : {}),
        /* Never the legacy welcome mail: it carries a password, and this
           product invites people to Orbit with a bound link instead. The
           checkbox below sends that link, not this. */
        send_email: false,
      });
      await adopt(res.client_id);
      aaiApi.setAaiCompany(res.client_id);
      let word = `${company.trim()} saved as a Prospect.`;
      if (sendEmail) {
        try {
          const invite = await sendIntakeInvite(res.client_id, LINK_DAYS);
          word = `${company.trim()} saved, and the invite is on its way to ${invite.email}.`;
        } catch (err) {
          /* The client exists either way, so this reports rather than fails:
             losing the record because an email bounced would be worse. */
          word = `${company.trim()} saved, but the invite could not be sent. Send it from their Overview.`;
          toast.warning((err as Error).message || 'The invite could not be sent.');
        }
      }
      toast.success(`${word}${site ? ' Orbit is reading the site now.' : ''}`);
      onCreated({ clientId: res.client_id, company: company.trim(), email: ownerEmail.trim(), website: site });
    } catch (err) {
      setError((err as Error).message || 'Could not create this client.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageBody>
      <PageHead eyebrow="Add client" title="Add a client" sub="Name the company and its owner. They are added as a Prospect and you land in their Orbit Room, where the journey starts. A website you give is read in the background." />
      <Panel>
        <PanelHead title="The company and its owner" desc="Nothing is emailed unless you ask for it below. You can send the invite later from their Overview." />
        <div className="grid gap-4 px-5 pb-5 md:grid-cols-2 md:px-6">
          <Field label="Company name"><Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Harbor Freight" autoFocus /></Field>
          <Field label="Website (optional)" hint="Orbit reads it in the background."><Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="harborfreight.com" /></Field>
          <Field label="Owner's name (optional)"><Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Priya Nair" autoComplete="off" /></Field>
          <Field label="Owner's email" hint={emailHint}><Input type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="priya@harborfreight.com" autoComplete="off" aria-invalid={emailCheck.state === 'invalid' || emailCheck.state === 'taken' || emailCheck.state === 'conflict'} /></Field>
          <Field label="Owner's phone (optional)"><Input type="tel" value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)} placeholder="+1 555 010 2030" autoComplete="off" /></Field>
        </div>
        <div className="px-5 pb-5 md:px-6">
          <label htmlFor="add-client-send-invite" className="flex cursor-pointer items-start gap-2.5 text-[13px] text-ink">
            <input
              id="add-client-send-invite"
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              aria-labelledby="add-client-send-invite-label"
              aria-describedby="add-client-send-invite-hint"
              className="mt-0.5 h-4 w-4 shrink-0 accent-black"
            />
            <span>
              <span id="add-client-send-invite-label">Email the Orbit invite now</span>
              <span id="add-client-send-invite-hint" className="mt-0.5 block text-[12px] leading-[19px] text-ink-dim">
                Leave this off to add them as a Prospect and invite them when you are ready, from their Overview.
              </span>
            </span>
          </label>
        </div>
        {error && <div className="px-5 pb-4 md:px-6"><ErrorNote message={error} /></div>}
        <div className="flex flex-wrap gap-2 border-t border-line-soft px-5 py-4 md:px-6">
          <Action arrow onClick={submit} disabled={!valid || busy} reason={reason}>{busy ? 'Creating the client' : 'Create and open their Orbit Room'}</Action>
          <Action variant="quiet" to="/partner/clients">Cancel</Action>
        </div>
      </Panel>
    </PageBody>
  );
};

const PartnerDeck: React.FC<{
  created: Created;
  stage: Stage;
  onFrameworkDone: () => void;
  onFinished: () => void;
}> = ({ created, stage, onFrameworkDone, onFinished }) => {
  const [deck, setDeck] = useState<OrbitDeckResponse | null>(null);
  const [overview, setOverview] = useState<OrbitOverview | null>(null);
  const [people, setPeople] = useState<OrbitRoomPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [learned, setLearned] = useState<DeckLearned[]>([{ text: 'Client created. The six starter cards are yours first.' }]);
  const [auto, setAuto] = useState(0);
  const [removed, setRemoved] = useState<string[]>([]);
  const [added, setAdded] = useState<DeckContextItem[]>([]);
  const searchTools = useMemo(() => searchToolsFor(), []);
  const contextItems = useMemo<DeckContextItem[]>(() => {
    if (!created.website) return added;
    return [{ key: 'site', label: hostOf(created.website) || created.website, kind: 'website' }, ...added];
  }, [created.website, added]);

  /*
   * The read, narrated, on the client's own card.
   *
   * The same seven passes a person signing up watches, on a company that
   * already exists, so what it finds is written to the client's dossier rather
   * than held in a browser. It starts when the partner finishes their own
   * first card, owns the card after it for the walk through its seven passes,
   * then reports beside the deck while the partner answers. Cards it answers
   * leave on their own, as they already did.
   */
  const [work, setWork] = useState<DeckWork | null>(null);
  const [reading, setReading] = useState(false);
  const [handedOff, setHandedOff] = useState(false);
  // Did the stream narrate at all? Without a walk there is nothing to wait on.
  const narrated = useRef(false);
  const walkOver = useRef(false);

  /*
   * The deck has shown every pass it is going to show, so the read card leaves
   * and the starter cards are next. This is the only way past that card: the
   * read finishing does not take it, because a cached site can come back in
   * eight seconds and the walk is still mid-way through its passes.
   */
  const readWalkDone = () => {
    if (walkOver.current) return;
    walkOver.current = true;
    setHandedOff(true);
    setRemoved((r) => (r.includes(READ_CARD) ? r : [...r, READ_CARD]));
  };

  const noteStep = (step: ReadStep) => setWork((prev) => {
    if (!prev) return prev;
    const steps: DeckWorkStep[] = [...prev.steps];
    const at = steps.map((x) => x.key).lastIndexOf(step.key);
    if (at >= 0 && steps[at].state === 'doing') steps[at] = step;
    else steps.push(step);
    return { ...prev, steps };
  });

  // A report is folded into the row with its key: data about a pass, never a
  // cue about which pass the walk should be showing. Both counters only climb.
  const notePass = (pass: ReadPass) => {
    narrated.current = true;
    setWork((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        passes: mergePass(prev.passes || [], pass),
        pagesRead: Math.max(prev.pagesRead || 0, pass.pages_read),
      };
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      aaiApi.setAaiCompany(created.clientId);
      const [d, o] = await Promise.all([aaiApi.getOrbitDeck('partner'), aaiApi.getOrbitOverview().catch(() => null)]);
      setDeck(d.data);
      if (o) setOverview(o.data);
      try {
        const p = await aaiApi.getOrbitRoomPeople();
        setPeople(p.data.people);
      } catch {
        setPeople([]);
      }
    } catch {
      setError('Could not deal the cards.');
    } finally {
      setLoading(false);
    }
  }, [created.clientId]);
  useEffect(() => { load(); }, [load]);

  const serverCards = useMemo(() => (deck ? toDeckCards(deck, people).filter((c) => c.input !== 'confirm_org') : []), [deck, people]);
  const frameworkCard = useMemo<DeckCardModel>(() => ({
    id: 'framework_setup', tag: 'confirm', tagLabel: 'Solution Partner only', title: 'Which questions does this client get?',
    why: 'Your opening questions decide what Orbit asks first. The owner never sees this card.', input: 'say', noSkip: true,
    footNote: 'Adjusting is optional. The default underneath is always Agents Anywhere\'s.',
    body: (ctx) => <FrameworkBody clientId={created.clientId} company={created.company} {...ctx} />,
  }), [created.clientId, created.company]);
  const inviteCard = useMemo<DeckCardModel>(() => ({
    id: 'invite', tag: 'confirm', tagLabel: 'Last card', title: `Invite ${created.email}?`,
    why: 'Nothing has been emailed yet. The owner picks up where you left off; Orbit asks them only what is still missing.', input: 'say', noSkip: true,
    footNote: 'You can send or resend the invite any time from the client\'s Overview.',
    body: (ctx) => <InviteBody created={created} {...ctx} />,
  }), [created]);
  const readCard = useMemo<DeckCardModel>(() => ({
    id: READ_CARD, tag: 'evidence', tagLabel: 'Reading the client', title: `Orbit is reading ${hostOf(created.website)}`,
    why: 'Seven passes over their site and the open web. What it finds is written to this client, so the cards it answers leave the deck.',
    input: 'say', noSkip: true,
    footNote: 'It keeps reading while you answer. Nothing waits on it.',
    body: () => null,
  }), [created.website]);
  const cards = useMemo(() => {
    // Picked back up: the framework card was answered before the partner left,
    // and the read it started has been running since. Neither is dealt twice.
    if (stage === 'deck') return [...serverCards, inviteCard];
    if (!created.website) return [frameworkCard, ...serverCards, inviteCard];
    return [frameworkCard, readCard, ...serverCards, inviteCard];
  }, [frameworkCard, readCard, serverCards, inviteCard, created.website, stage]);
  const isLocal = (id: string) => id === 'framework_setup' || id === 'invite' || id === READ_CARD;

  /*
   * Kicked off when the partner finishes their own card, and never awaited:
   * the panel belongs to the NEXT card, so waiting here would hold the deck on
   * the card before the one that has to show the walk.
   */
  const startRead = () => {
    const site = created.website;
    if (!site) return;
    walkOver.current = false;
    narrated.current = false;
    setHandedOff(false);
    setReading(true);
    setWork({
      title: `Reading ${hostOf(site)}`,
      cardId: READ_CARD,
      note: 'Seven passes over their site and the open web. One card each, as they land.',
      steps: [],
      site,
      pagesRead: 0,
      passes: READ_PASSES.map((p) => ({ ...p, state: 'waiting', pages: 0, found: 0, line: '' })),
    });
    // Nothing but the sequence moves the deck on, so a panel that never
    // reports at all cannot strand the partner on the read card.
    window.setTimeout(readWalkDone, HANDOFF_CAP);
    companySiteReadStream(site, created.clientId, noteStep, notePass)
      .then((read) => {
        const kept = read.written || 0;
        let line = `${hostOf(site)} read: ${read.facts_total} facts from ${read.pages_read || 0} pages`;
        if (kept > 0) line = `${line}, ${kept} written to the client`;
        setLearned((l) => [{ text: `${line}.`, gold: true }, ...l].slice(0, 6));
        // A browser that would not stream still gets the read, in one piece at
        // the end. Its per-pass rows go in as data, so the passes the sequence
        // has not reached yet still show what they found.
        if (!narrated.current) setWork((prev) => fillPasses(prev, read));
        // Not load(): that shows the dealing state again, which unmounts the
        // deck and loses which cards the partner has already answered.
        aaiApi.getOrbitOverview().then((o) => setOverview(o.data)).catch(() => undefined);
        return refreshDeck(READ_CARD);
      })
      .catch(() => {
        setLearned((l) => [{ text: `Could not read ${hostOf(site)} just now. It is read again from the client's own page.` }, ...l].slice(0, 6));
        // Not a teardown: the panel closes through its own last card saying
        // what happened, which is the only way past it that looks deliberate.
        setWork((prev) => stopWork(prev, `Orbit could not finish reading ${hostOf(site)}. It is read again from the client's own page.`));
      })
      .finally(() => setReading(false));
  };

  // The website read lands while the partner flips: cards it answered leave
  // the deck the next time anything is saved.
  const refreshDeck = async (justAnswered: string) => {
    try {
      const fresh = await aaiApi.getOrbitDeck('partner');
      const after = new Set(fresh.data.cards.map((c) => c.id));
      const gone = cards.map((c) => c.id).filter((id) => !isLocal(id) && !after.has(id) && id !== justAnswered);
      if (gone.length) {
        setAuto((n) => n + gone.length);
        setRemoved((r) => [...r, ...gone]);
        setLearned((l) => [{ text: `What Orbit has read answered ${gone.length} card${gone.length === 1 ? '' : 's'} for you.`, gold: true }, ...l].slice(0, 6));
      }
      setDeck(fresh.data);
    } catch {
      // The deck on screen stays as it is.
    }
  };
  const readProgress = useMemo(() => {
    if (!reading || !handedOff || !work || !work.passes) return null;
    const passes = work.passes;
    const done = passes.filter((p) => LANDED.includes(p.state)).length;
    const doing = passes.find((p) => !LANDED.includes(p.state));
    let label = 'Finishing the read';
    if (doing) label = doing.title;
    return { done, total: passes.length, doing: label };
  }, [reading, handedOff, work]);
  const rail = useMemo(() => {
    const base = railFromOverview(deck ? deck.site : '', overview, learned, auto, deck ? deck.counts : undefined);
    return { ...base, site: base.site || created.website, read: readProgress };
  }, [deck, overview, learned, auto, readProgress, created.website]);

  const onAnswer = async (card: DeckCardModel, answer: DeckAnswer) => {
    if (card.id === 'framework_setup') {
      onFrameworkDone();
      setLearned((l) => [{ text: 'Dossier settled for this client. The starter cards follow it.' }, ...l].slice(0, 6));
      refreshDeck(card.id);
      startRead();
      return;
    }
    if (card.id === READ_CARD) return;
    if (card.id === 'invite') {
      setLearned((l) => [{ text: 'Invite on its way. The owner opens the same deck, minus what you answered.', gold: true }, ...l].slice(0, 6));
      return;
    }
    if (card.input === 'confirm') {
      if (answer.how !== 'edited') {
        const items = (answer.confirm || []).map((d) => ({ field_key: d.field_key, scope: d.scope || '', correct: d.action === 'correct', skip: d.action === 'skip', value: d.value }));
        await aaiApi.confirmOrbitFacts(items);
        setLearned((l) => [{ text: 'Confirmed on the client\'s behalf. The owner sees these as to confirm, from you.', gold: true }, ...l].slice(0, 6));
      }
      return;
    }
    const askId = askIdOf(card);
    if (!askId) return;
    if (answer.how === 'later') return;
    try {
      await aaiApi.answerOrbitAsk(askId, answer.value);
      setLearned((l) => [{ text: `${card.title.replace(/\?$/, '')}: answered for the client. The owner sees it as to confirm.` }, ...l].slice(0, 6));
      aaiApi.getOrbitOverview().then((o) => setOverview(o.data)).catch(() => undefined);
      refreshDeck(card.id);
    } catch {
      toast.error('Could not save that for the client.');
      throw new Error('answer failed');
    }
  };
  /*
   * Something handed to Orbit instead of answered. It is read the same way the
   * website was, so the cards it answers leave the deck rather than being put
   * to the partner anyway. The panel keeps what was typed when this throws.
   */
  const absorb = async (label: string, kind: string, work: () => Promise<{ facts: number; queued: boolean }>) => {
    let read: { facts: number; queued: boolean };
    try {
      read = await work();
    } catch {
      toast.error(`Could not read ${label}.`);
      throw new Error('context failed');
    }
    setAdded((a) => [...a, { key: `${kind}:${a.length}:${label}`, label, kind }]);
    let line = `${label} read: ${read.facts} facts for the client.`;
    if (read.queued) line = `${label} is being read now. What it answers leaves the deck on its own.`;
    setLearned((l) => [{ text: line, gold: true }, ...l].slice(0, 6));
    aaiApi.getOrbitOverview().then((o) => setOverview(o.data)).catch(() => undefined);
    await refreshDeck('');
  };
  const context: DeckContextState = {
    items: contextItems,
    onFile: (file) => absorb(file.name, 'document', async () => {
      const res = await readDocument(file, created.clientId);
      return { facts: res.written, queued: false };
    }),
    onNote: (text) => absorb('Pasted notes', 'note', async () => {
      const res = await ingest('paste', text, created.clientId);
      return { facts: (res.deltas || []).length, queued: res.mode === 'async' };
    }),
    onLink: (link) => absorb(hostOf(link) || link, 'link', async () => {
      const res = await ingest('url', link, created.clientId);
      return { facts: (res.deltas || []).length, queued: res.mode === 'async' };
    }),
  };

  const onSkip = async (card: DeckCardModel) => {
    if (card.id === 'invite') {
      setLearned((l) => [{ text: 'Invite left for later. The Overview keeps a reminder until it goes out.' }, ...l].slice(0, 6));
      return;
    }
    setLearned((l) => [{ text: `Left for the owner: "${card.title.replace(/\?$/, '')}" becomes one of their first cards.` }, ...l].slice(0, 6));
  };
  const onDocument = async (card: DeckCardModel, file: File) => {
    try {
      const before = new Set(cards.map((c) => c.id));
      const res = await readDocument(file, created.clientId);
      const fresh = await aaiApi.getOrbitDeck('partner');
      const after = new Set(fresh.data.cards.map((c) => c.id));
      const gone = Array.from(before).filter((id) => !after.has(id) && id !== card.id);
      setAuto((n) => n + gone.length);
      setLearned((l) => [{ text: `${file.name} read: ${res.written} facts.${gone.length ? ` It answered ${gone.length} card${gone.length === 1 ? '' : 's'}.` : ''}`, gold: true }, ...l].slice(0, 6));
      setRemoved((r) => [...r, ...gone]);
      setDeck(fresh.data);
      aaiApi.getOrbitOverview().then((o) => setOverview(o.data)).catch(() => undefined);
      return { removed: gone };
    } catch {
      toast.error('Could not read that file.');
      return undefined;
    }
  };

  if (loading) return <PageBody><Loading label="Dealing the cards" /></PageBody>;
  if (error) return <PageBody><ErrorNote message={error} /></PageBody>;

  const head = (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-line bg-white px-4 py-3 text-[12.5px] text-ink-dim">
      <Chip>The cards for this client</Chip>
      <span className="min-w-0 flex-1">Whatever you answer is shown to the owner as to confirm; whatever you leave becomes their first cards.</span>
      <Action size="sm" variant="quiet" to={frameworkPath(created.clientId, created.company, deckPath(created.clientId))}>Edit this client's questions</Action>
    </div>
  );

  return (
    <PageBody>
      <PageHead eyebrow={`Add client · ${created.company}`} title="The starter cards" sub="The same cards the owner would see, with one of yours first. Orbit reads the website and anything you drop while you flip through, and removes any card it can answer itself. The last card is the invite." actions={<Action variant="quiet" to={`/partner/framework?client_name=${encodeURIComponent(created.company)}&back=${encodeURIComponent(deckPath(created.clientId))}`}>Your opening questions</Action>} />
      <OrbitDeck
        cards={cards}
        rail={rail}
        context={context}
        head={head}
        removedIds={removed}
        transcribe={transcribeFor(created.clientId)}
        searchTools={searchTools}
        labels={{ primary: 'Answer for the client', skip: 'Leave for the owner', skipWord: 'leave' }}
        work={handedOff ? null : work}
        onReadDone={readWalkDone}
        onAnswer={onAnswer}
        onSkip={onSkip}
        onDocument={onDocument}
        onDone={onFinished}
      />
    </PageBody>
  );
};

const AddClient: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const resuming = params.get('client_id') || '';
  const [created, setCreated] = useState<Created | null>(null);
  const [stage, setStage] = useState<Stage>('framework');
  const [lost, setLost] = useState(false);

  useEffect(() => {
    if (!resuming || created) return;
    const row = unstash(resuming);
    if (!row) { setLost(true); return; }
    setStage(row.stage);
    setCreated(row.created);
  }, [resuming, created]);

  // The client exists either way, so the one thing this must never do is offer
  // the empty form again and let the partner create it a second time.
  useEffect(() => {
    if (!lost) return;
    toast.info('That setup was not picked up on this browser. The client is on your list.');
    navigate('/partner/clients', { replace: true });
  }, [lost, navigate]);

  /* Straight to the client, where the Orbit Room is. The starter deck is no
     longer a step of adding a client. */
  const start = () => {
    navigate('/partner/client', { replace: true });
  };

  if (!created) {
    if (resuming) return <PageBody><Loading label="Picking up where you left off" /></PageBody>;
    return <StartForm onCreated={start} />;
  }
  return (
    <PartnerDeck
      created={created}
      stage={stage}
      onFrameworkDone={() => { setStage('deck'); stash(created, 'deck'); }}
      onFinished={() => {
        dropStash(created.clientId);
        toast.success(`${created.company}: the journey starts at the Overview, with phase 1 open.`);
        navigate('/partner/client');
      }}
    />
  );
};

export default AddClient;
